import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { CalendarToday, LocationOn, AccessTime, WbSunny, Bedtime } from "@mui/icons-material";

const ProgramHeader = ({ program }) => {
    const { i18n } = useTranslation();
    const lang = i18n.language;


    return (
        <Box
            sx={{
                mb: 4,
                '@media print': {
                    pageBreakAfter: 'auto',
                    marginBottom: '10px',
                    breakInside: 'avoid',
                }
            }}
        >
            {/* Cover Image with Overlay */}
            {program.cover_image && (
                <Box
                    sx={{
                        width: "100%",
                        height: 400,
                        borderRadius: 2,
                        overflow: "hidden",
                        mb: 4,
                        position: "relative",
                        border: 1,
                        borderColor: "divider"
                    }}
                >
                    <img
                        src={program.cover_image.url}
                        alt={program.title[lang]}
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                        }}
                    />

                    {/* Overlay Gradient */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: "linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.6) 100%)",
                        }}
                    />

                    {/* Title Overlay */}
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            p: 4,
                            color: "white",
                            '@media print': {
                                padding: '16px',
                            }
                        }}
                    >
                        <Typography
                            variant="h3"
                            fontWeight="bold"
                            sx={{
                                fontSize: { xs: '2rem', md: '2.5rem' },
                                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                                '@media print': {
                                    fontSize: '20px',
                                }
                            }}
                        >
                            {program.title[lang]}
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Trip Details with Icons */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 4 }}>
                    {/* Dates */}
                    <Box sx={{ textAlign: 'left' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml:-0.5 }}>
                                <CalendarToday sx={{ fontSize: 20, color: '#42a5f5' }} />
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff9800', fontSize: '17px' }}>
                                    {lang === 'ar' ? "التواريخ" : "Dates"}
                                </Typography>
                            </Box>
                            <Typography variant="body1" sx={{ textAlign: 'left', fontWeight: 'bold', color: '#424242' }}>
                                {format(new Date(program.start_date), "dd MMM yyyy")} - {format(new Date(program.end_date), "dd MMM yyyy")}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Duration */}
                    <Box sx={{ textAlign: 'left' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AccessTime sx={{ fontSize: 20, color: '#ff9800' }} />
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff9800', fontSize: '17px' }}>
                                    {lang === 'ar' ? "المدة" : "Duration"}
                                </Typography>
                            </Box>
                            <Typography variant="body1" sx={{ textAlign: 'left', fontWeight: 'bold', color: '#424242', fontSize: '18px', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <WbSunny sx={{ fontSize: 16, color: '#ff9800' }} />
                                    {program.durations} {lang === 'ar' ? 'أيام' : 'Days'}
                                </Box>
                                <Box component="span" sx={{ mx: 1 }}>|</Box>
                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Bedtime sx={{ fontSize: 16, color: '#9c27b0' }} />
                                    {program.nights} {lang === 'ar' ? 'ليالي' : 'Nights'}
                                </Box>
                            </Typography>
                        </Box>
                    </Box>

                    {/* Destinations */}
                    <Box sx={{ textAlign: 'left' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1,ml:-1 }}>
                                <LocationOn sx={{ fontSize: 20, color: '#4caf50' }} />
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff9800', fontSize: '17px' }}>
                                    {lang === 'ar' ? "الوجهات" : "Destinations"}
                                </Typography>
                            </Box>
                            <Typography variant="body1" sx={{ textAlign: 'left', fontWeight: 'bold', color: '#424242', fontSize: '18px' }}>
                                {(() => {
                                    const cities = program?.cities;
                                    if (!cities || !Array.isArray(cities) || cities.length === 0) return 'N/A';
                                    const names = cities
                                        .sort((a, b) => a.order - b.order)
                                        .map(c => `\u2068${c.name?.[lang] || c.name?.en || c.name?.ar || ''}\u2069`);

                                    if (names.length === 0) return 'N/A';
                                    if (names.length === 1) return names[0];

                                    const separator = lang === 'ar' ? '، ' : ', ';
                                    const lastSeparator = lang === 'ar' ? ' و' : ' & ';

                                    return names.slice(0, -1).join(separator) + lastSeparator + names[names.length - 1];
                                })()}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ProgramHeader;
